import React, { useState, useEffect, useCallback } from 'react';
import { clinicianService } from '../services/api';
import { Card, Button, Badge } from './ui';
import { AlertCircle, CheckCircle2, Clock, TrendingDown } from 'lucide-react';
import AlertSystemDisclaimer from './AlertSystemDisclaimer';

const ClinicianAlerts = ({ selectedTab = 'active' }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [filterSeverity, setFilterSeverity] = useState(null);
  const [expandedAlertId, setExpandedAlertId] = useState(null);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await clinicianService.getAlerts(
        selectedTab || 'active',
        filterSeverity,
        50,
        7
      );

      setAlerts(response.data.data.alerts || []);
      setActiveCount(response.data.data.activeCount || 0);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load alerts'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedTab, filterSeverity]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleUpdateAlert = async (alertId, status, clinicalNotes = null) => {
    try {
      const payload = { status };
      if (clinicalNotes) {
        payload.clinicalNotes = clinicalNotes;
      }

      await clinicianService.updateAlert(alertId, payload);
      setMessage({ type: 'success', text: 'Alert updated' });
      loadAlerts();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update alert'
      });
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'safetyThreshold':
        return <AlertCircle className="h-5 w-5" />;
      case 'lapseReport':
        return <AlertCircle className="h-5 w-5" />;
      case 'trendPattern':
        return <TrendingDown className="h-5 w-5" />;
      default:
        return <Clock className="h-5 w-5" />;
    }
  };

  const getAlertDescription = (alert) => {
    const ctx = alert.clinicalContext || {};

    switch (alert.alertType) {
      case 'safetyThreshold':
        return (
          <div className="text-sm text-neutral-600 mt-2">
            <p>Mood: {ctx.moodScale}/10 | Challenge: {ctx.cravingScale}/10</p>
            <p className="text-xs mt-1">
              {ctx.consecutiveDaysTriggering} consecutive {ctx.moodScale <= 2 ? 'low mood' : 'high challenge'} days
            </p>
          </div>
        );
      case 'lapseReport':
        return (
          <div className="text-sm text-neutral-600 mt-2">
            <p><strong>{ctx.substanceBehavior}</strong> on {new Date(ctx.lapseDate).toLocaleDateString()}</p>
          </div>
        );
      case 'trendPattern':
        return (
          <div className="text-sm text-neutral-600 mt-2">
            <p>
              {ctx.metricName === 'mood' ? 'Mood' : 'Challenge'} change: {ctx.changeAmount > 0 ? '+' : ''}{ctx.changeAmount} points over {ctx.timeframeDays} days
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <p className="text-neutral-500">Loading alerts...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <AlertSystemDisclaimer variant="compact" />
      
      {message && (
        <div
          className={`rounded-soft border p-3 text-sm ${
            message.type === 'success'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-error-200 bg-error-50 text-error-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-l-red-500">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-neutral-900">Critical Alerts</h3>
            <p className="text-sm text-neutral-600 mt-1">
              {activeCount} active alert{activeCount !== 1 ? 's' : ''} requiring clinician attention
            </p>
          </div>
          <Badge variant="danger" size="lg">
            {activeCount}
          </Badge>
        </div>
      </Card>

      <div className="flex gap-2 mb-4">
        <Button
          variant={filterSeverity === null ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterSeverity(null)}
        >
          All Severity
        </Button>
        <Button
          variant={filterSeverity === 'critical' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterSeverity('critical')}
        >
          Critical
        </Button>
        <Button
          variant={filterSeverity === 'high' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterSeverity('high')}
        >
          High
        </Button>
        <Button
          variant={filterSeverity === 'moderate' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setFilterSeverity('moderate')}
        >
          Moderate
        </Button>
      </div>

      {alerts.length === 0 ? (
        <Card className="text-center py-12">
          <CheckCircle2 className="h-12 w-12 mx-auto text-success-500 mb-4" />
          <p className="text-lg font-semibold text-neutral-800">No {selectedTab} alerts</p>
          <p className="text-sm text-neutral-600 mt-2">
            {selectedTab === 'active' 
              ? 'All clients appear to be stable.' 
              : 'No alerts match your current filters.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <Card
              key={alert._id}
              className={`border-l-4 ${getSeverityColor(alert.severity)} cursor-pointer transition-all`}
              onClick={() =>
                setExpandedAlertId(expandedAlertId === alert._id ? null : alert._id)
              }
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex-shrink-0 mt-1">
                    {getAlertIcon(alert.alertType)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-neutral-900">{alert.message}</h4>
                      <Badge variant={alert.severity}>
                        {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                      </Badge>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">
                      {alert.client?.firstName} {alert.client?.lastName} • Day {alert.client?.currentDay}
                    </p>
                    {getAlertDescription(alert)}
                    {alert.clinicalNotes && (
                      <div className="mt-3 p-2 bg-white/50 rounded text-xs">
                        <p className="font-semibold text-neutral-700">Clinical Notes:</p>
                        <p className="text-neutral-600">{alert.clinicalNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="text-xs text-neutral-500">
                    {new Date(alert.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {expandedAlertId === alert._id && alert.status === 'active' && (
                <div className="mt-4 pt-4 border-t border-current/20 space-y-2">
                  <p className="text-sm font-semibold text-neutral-800">Clinician Response:</p>
                  <textarea
                    id={`notes-${alert._id}`}
                    className="w-full px-3 py-2 border border-current rounded text-sm"
                    placeholder="Add clinical notes (optional)"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        const notes = document.getElementById(`notes-${alert._id}`)?.value;
                        handleUpdateAlert(alert._id, 'reviewed', notes);
                      }}
                    >
                      Mark Reviewed
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        const notes = document.getElementById(`notes-${alert._id}`)?.value;
                        handleUpdateAlert(alert._id, 'resolved', notes);
                      }}
                    >
                      Mark Resolved
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClinicianAlerts;
